import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatCounter } from '@/components/home/StatCounter';
import { VerticalPillarCard } from '@/components/home/VerticalPillarCard';
import { HERO_IMAGE, IMG_MATERIALS, IMG_SPARE, IMG_MACHINES, IMG_PAINTS } from '@/components/home/helpers';
import {
  Sparkles, Search, ArrowRight, Factory, Wrench, Package, ShieldCheck, BadgeCheck,
  Leaf, ChevronRight, Palette, Recycle,
} from 'lucide-react';

export const HeroSection = ({
  sustainability,
  heroCO2Display,
  heroQuery,
  setHeroQuery,
  heroVertical,
  setHeroVertical,
  onHeroSearch,
  onPostWantedAd,
  machines,
  spareParts,
  paints,
  verticalCountsLoading,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <section
      className="relative border-b border-emerald-900/20 overflow-hidden bg-slate-950"
      data-testid="hero-section"
    >
      {/* Base industrial photo */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        aria-hidden
      />
      {/* Emerald color overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgba(4,47,46,0.92) 0%, rgba(6,78,59,0.88) 55%, rgba(15,23,42,0.95) 100%)',
        }}
        aria-hidden
      />
      {/* Grain texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* LEFT: Value prop */}
        <div className="lg:col-span-7 flex flex-col text-white">
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 px-2.5 py-1 backdrop-blur-sm"
              data-testid="hero-badge"
            >
              <Leaf className="h-3 w-3" />
              {t('home.hero.badge')}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-100/70">
              <BadgeCheck className="h-3 w-3" /> {t('home.hero.verified')}
            </span>
          </div>

          <h1 className="font-manrope text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.92]">
            <span className="block text-white">{t('home.hero.headlineA')}</span>
            <span className="block text-white">{t('home.hero.headlineB')}</span>
            <span className="block relative inline-block">
              <span className="relative z-10 text-emerald-300">{t('home.hero.headlineC')}</span>
              <span className="absolute inset-x-0 bottom-1 h-2 md:h-3 bg-emerald-400/30 -z-0" aria-hidden />
            </span>
          </h1>
          <p className="text-base md:text-lg text-emerald-50/85 mt-5 max-w-xl leading-relaxed">
            {t('home.hero.subtitle')}
          </p>

          {/* LIVE IMPACT TICKER */}
          {sustainability && (
            <div
              className="mt-6 inline-flex items-center gap-3 self-start bg-white/5 border border-emerald-400/20 backdrop-blur-sm px-4 py-2.5"
              data-testid="hero-impact-ticker"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-300">
                {t('home.sustain.liveImpact')}
              </span>
              <span className="text-white/70 text-xs hidden sm:inline">{t('home.hero.impactPrefix')}</span>
              <StatCounter
                value={heroCO2Display.value}
                decimals={heroCO2Display.decimals}
                suffix={heroCO2Display.unit}
                className="text-white text-lg font-bold"
              />
              <span className="text-white/60 text-xs">{t('home.hero.impactSuffix')}</span>
            </div>
          )}

          {/* Search bar */}
          <form
            onSubmit={onHeroSearch}
            className="mt-6 bg-white border border-white/10 shadow-2xl flex items-stretch w-full max-w-xl focus-within:ring-2 focus-within:ring-emerald-400 transition-all"
            data-testid="hero-search-form"
          >
            <Select value={heroVertical} onValueChange={setHeroVertical}>
              <SelectTrigger
                className="rounded-none border-0 border-r border-slate-200 w-36 sm:w-44 h-12 bg-slate-50 font-semibold text-slate-800 focus:ring-0 focus:ring-offset-0"
                data-testid="hero-vertical-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="machines">{t('home.hero.verticalMachines')}</SelectItem>
                <SelectItem value="spare_parts">{t('home.hero.verticalSpareParts')}</SelectItem>
                <SelectItem value="materials">{t('home.hero.verticalMaterials')}</SelectItem>
                <SelectItem value="paints">{t('home.hero.verticalPaints')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={heroQuery}
              onChange={(e) => setHeroQuery(e.target.value)}
              placeholder={t('home.hero.searchPlaceholder')}
              className="rounded-none border-0 h-12 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm text-slate-900"
              data-testid="hero-search-input"
            />
            <Button
              type="submit"
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-5 gap-2 border-0"
              data-testid="hero-search-submit"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">{t('home.hero.searchBtn')}</span>
            </Button>
          </form>

          {/* Quick CTAs */}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <button
              onClick={() => (user ? onPostWantedAd() : navigate('/login?next=/wanted'))}
              data-testid="hero-post-wanted-ad-btn"
              className="inline-flex items-center gap-1.5 font-semibold text-orange-400 hover:text-orange-300 underline underline-offset-4 decoration-orange-400/40 hover:decoration-orange-400"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {t('home.hero.ctaPostWanted')}
            </button>
            <span className="text-white/20" aria-hidden>|</span>
            <button
              onClick={() => navigate('/for-partners')}
              data-testid="hero-partners-link"
              className="inline-flex items-center gap-1 text-emerald-100/80 hover:text-white"
            >
              {t('home.hero.ctaForSellers')} <ChevronRight className="h-3 w-3" />
            </button>
            <span className="text-white/20" aria-hidden>|</span>
            <button
              onClick={() => navigate('/user-guide')}
              data-testid="hero-guide-link"
              className="inline-flex items-center gap-1 text-emerald-100/80 hover:text-white"
            >
              {t('home.hero.ctaGuide')} <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          {/* Trust row */}
          <div className="mt-auto pt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-emerald-100/60">
            <span className="inline-flex items-center gap-1.5">
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-300" />
              {t('home.hero.trustVies')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
              {t('home.hero.trustCompanies')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ArrowRight className="h-3.5 w-3.5 text-emerald-300" />
              {t('home.hero.trustCircular')}
            </span>
          </div>
        </div>

        {/* RIGHT: 4-pillar bento (2x2 grid) */}
        <div className="lg:col-span-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <VerticalPillarCard
              label={t('home.pillar.machines.label')}
              title={t('home.pillar.machines.title')}
              description={t('home.pillar.machines.desc')}
              accentColor="#2563EB"
              accentBg="#EFF6FF"
              accentText="#1E3A8A"
              icon={Factory}
              imageUrl={IMG_MACHINES}
              to="/listings?vertical=machines"
              testId="pillar-machines"
              metric={{
                value: verticalCountsLoading ? '…' : machines.length > 0 ? `${machines.length}+` : '0',
                label: t('home.pillar.machines.metric'),
              }}
            />
            <VerticalPillarCard
              label={t('home.pillar.spareParts.label')}
              title={t('home.pillar.spareParts.title')}
              description={t('home.pillar.spareParts.desc')}
              accentColor="#334155"
              accentBg="#F1F5F9"
              accentText="#0F172A"
              icon={Wrench}
              imageUrl={IMG_SPARE}
              to="/listings?vertical=spare_parts"
              testId="pillar-spare-parts"
              metric={{
                value: verticalCountsLoading ? '…' : spareParts.length > 0 ? `${spareParts.length}+` : '0',
                label: t('home.pillar.spareParts.metric'),
              }}
            />
            <VerticalPillarCard
              label={t('home.pillar.materials.label')}
              title={t('home.pillar.materials.title')}
              description={t('home.pillar.materials.desc')}
              accentColor="#10B981"
              accentBg="#ECFDF5"
              accentText="#065F46"
              icon={Package}
              imageUrl={IMG_MATERIALS}
              to="/search"
              testId="pillar-materials"
              metric={{
                value: sustainability ? sustainability.total_materials_listed : '—',
                label: t('home.pillar.materials.metric'),
              }}
            />
            <VerticalPillarCard
              label={t('home.pillar.paints.label')}
              title={t('home.pillar.paints.title')}
              description={t('home.pillar.paints.desc')}
              accentColor="#C026D3"
              accentBg="#FAE8FF"
              accentText="#86198F"
              icon={Palette}
              imageUrl={IMG_PAINTS}
              to="/listings?vertical=paints"
              testId="pillar-paints"
              metric={{
                value: verticalCountsLoading ? '…' : paints.length > 0 ? `${paints.length}+` : '0',
                label: t('home.pillar.paints.metric'),
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
